import { Link } from "react-router-dom";

export default function ProductCard({ product }) {
  const { titel, url_slug, pris, beskrivelse, billede } = product;

  const rawCategory = product.terms?.kategori?.[0] || product.kategori || "";
  const rawStatus = product.terms?.status?.[0] || product.status || "";

  // Format category name for display
  const formatCategory = (cat) => {
    switch (cat) {
      case "mobler":
        return "Møbler";
      case "porcelan-og-keramik":
        return "Porcelæn & Keramik";
      case "toj":
        return "Tøj";
      case "design":
        return "Design";
      case "malerier":
        return "Malerier";
      case "diverse":
        return "Diverse";
      default:
        return cat || "Diverse";
    }
  };

  // Format status for display
  const formatStatus = (stat) => {
    switch (stat) {
      case "til-salg-i-butikken":
        return "Til salg i butikken";
      case "tilgaengelig":
        return "Tilgængelig";
      case "sporg-i-butikken":
        return "Spørg i butikken";
      case "solgt":
        return "Solgt";
      default:
        return stat || "Til salg i butikken";
    }
  };

  const statusDisplay = formatStatus(rawStatus);

  // Status badge styling
  const getStatusStyle = (stat) => {
    const formatted = formatStatus(stat);
    switch (formatted) {
      case "Tilgængelig":
      case "Til salg i butikken":
        return "bg-emerald-600 text-white";
      case "Solgt":
        return "bg-rose-600 text-white";
      case "Spørg i butikken":
      default:
        return "bg-amber-600 text-white";
    }
  };

  // Optimize image URL using Cloudflare resizing
  const originalImageUrl = Array.isArray(billede) ? billede[0] : billede;
  const optimizedImageUrl = originalImageUrl
    ? originalImageUrl.replace("assets.auxodev.dk/assets", "assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets")
    : "";

  // Dynamic specifications based on product fields
  const specs = [
    { label: "Kategori", value: formatCategory(rawCategory) },
    { label: "Pris", value: `${pris} kr.` },
    { label: "Status", value: statusDisplay },
    { label: "Placering", value: "Butikken i Nyborg" }
  ];

  return (
    <div className="group flex flex-col overflow-hidden bg-[#f5f3ed] border border-stone-200/60 transition-all duration-300 hover:shadow-md">
      {/* Product Image Header */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-stone-100">
        {optimizedImageUrl ? (
          <img
            src={optimizedImageUrl}
            alt={titel}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-dark-graphite/30">
            <i className="fa-solid fa-image text-4xl" />
          </div>
        )}

        {/* Overlay Category Badge */}
        <span
          className={`absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest shadow-sm ${getStatusStyle(
            rawStatus
          )}`}
        >
          {statusDisplay}
        </span>
      </div>

      {/* Card Body Container */}
      <div className="p-8 flex flex-col justify-between flex-grow min-h-[380px]">
        <div>
          {/* Title */}
          <h3 className="font-sans text-xl font-bold text-dark-graphite mb-3 leading-snug group-hover:text-brand-orange transition-colors">
            {titel}
          </h3>

          {/* Description */}
          <p className="text-xs text-dark-graphite-light leading-relaxed mb-6 line-clamp-3">
            {beskrivelse}
          </p>

          {/* Specifications Sub-Block */}
          <div className="mb-6">
            <h4 className="text-brand-orange text-[11px] font-extrabold tracking-widest uppercase mb-3">
              DETALJER
            </h4>
            <ul className="space-y-1.5">
              {specs.map((spec, idx) => (
                <li key={idx} className="text-xs text-dark-graphite-light flex items-start gap-2">
                  <i className="fa-solid fa-check text-brand-orange mt-0.5 shrink-0" />
                  <span>
                    <strong className="font-semibold text-dark-graphite">{spec.label}:</strong> {spec.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Card Footer Button Action */}
        <div className="mt-auto pt-4">
          {statusDisplay === "Solgt" ? (
            <button
              disabled
              className="w-full py-3 bg-stone-200 text-stone-400 text-center uppercase tracking-widest text-xs font-bold cursor-not-allowed"
            >
              Varen er solgt
            </button>
          ) : (
            <Link
              to={`/kontakt?produkt=${encodeURIComponent(titel)}`}
              className="block w-full py-3 bg-brand-orange hover:bg-brand-orange-hover text-white text-center uppercase tracking-widest text-xs font-bold transition-all"
            >
              <i className="fa-regular fa-comment-dots mr-2" />
              Spørg om varen
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
